import * as React from 'react';
import {
	ReactFlow,
	Background,
	Controls,
	MiniMap,
	Panel,
	useNodesState,
	useEdgesState,
	type Node,
	type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { css } from 'aphrodite';
import { nodeTypes } from '../../../../web/containers/AutomationFlows/nodes';
import { edgeTypes } from '../../../../web/containers/AutomationFlows/edges';
import {
	enrichNodesWithContacts,
	enrichEdgesWithContactsPreview,
} from '../../../../web/containers/AutomationFlows/utils/contactFlowMapping';
import { TestContact } from '../types';
import { styleSheet } from '../styles';
import { ReadOnlyCanvasToolbar } from './ReadOnlyCanvasToolbar';

interface ReadOnlyCanvasProps {
	nodes: Node[];
	edges: Edge[];
	selectedContacts: TestContact[];
}

export const ReadOnlyCanvas: React.FC<ReadOnlyCanvasProps> = ({
	nodes: initialNodes,
	edges: initialEdges,
	selectedContacts,
}) => {
	// Use managed state for nodes and edges to enable layout adjustments
	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);

	// Update nodes when initial nodes change or selectedContacts change
	React.useEffect(() => {
		// Use shared utility for API-driven contact-to-node mapping
		// TestContact is structurally compatible with ContactWithSteps
		let enriched = enrichNodesWithContacts(initialNodes, selectedContacts);

		// Add read-only flag to all nodes
		enriched = enriched.map(node => ({
			...node,
			data: {
				...node.data,
				isReadOnly: true,
			},
		}));

		setNodes(enriched);
	}, [initialNodes, selectedContacts, setNodes]);

	// Update edges when initial edges change or selectedContacts change
	React.useEffect(() => {
		// Use preview mode to show contacts on ALL edges they will traverse
		// TestContact is structurally compatible with ContactWithSteps
		const enriched = enrichEdgesWithContactsPreview(initialEdges, selectedContacts);

		setEdges(enriched);
	}, [initialEdges, selectedContacts, setEdges]);

	return (
		<div className={css(styleSheet.canvasContainer)} role='region' aria-label='Read-only automation flow visualization'>
			<ReactFlow
				nodes={nodes}
				edges={edges}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				nodesDraggable={false}
				nodesConnectable={false}
				elementsSelectable={true}
				zoomOnScroll={true}
				panOnScroll={true}
				zoomOnDoubleClick={false}
				fitView
				fitViewOptions={{
					padding: 0.2,
				}}
			>
				<Background />
				<Controls />
				<MiniMap />
				<Panel position='top-right'>
					<ReadOnlyCanvasToolbar />
				</Panel>
			</ReactFlow>

			{selectedContacts.length > 0 && (
				<div className={css(styleSheet.legend)}>
					<div className={css(styleSheet.legendTitle)}>Selected Contacts</div>
					{selectedContacts.map(testContact => {
						const stepCount = testContact.steps?.length || 0;
						return (
							<div key={testContact.contact.id} className={css(styleSheet.legendItem)}>
								<div className={css(styleSheet.legendColor)} style={{ backgroundColor: testContact.color }} />
								<span>
									{testContact.contact.firstName} {testContact.contact.lastName}
									{stepCount > 0 && <span className={css(styleSheet.legendStepCount)}> ({stepCount} steps)</span>}
								</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
